import React, { useState, useEffect, forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import { classNames, useGlobalId, useUpdateRef } from '@zambezi/sdk/common'
import RadioField from './RadioField'
import { getError, errorOptionsType, hintOptionsType } from './zambezi-helpers'
import Label from './Label'

/**
 * @type {React.ForwardRefRenderFunction<React.Ref<HTMLInputElement>, import('./interfaces').RadioGroupFieldProps>}
 * @param {import('./interfaces').RadioGroupFieldProps} props
 * @param {React.Ref<HTMLInputElement>} innerRef
 */
function radioGroupField (props, innerRef) {
  const {
    label,
    name,
    value,
    values,
    disabled,
    className,
    error,
    rules,
    errorOptions,
    errorMessageShown,
    hint,
    hintOptions,
    inputProps = {},
    onChange,
    onBlur,
    onFocus,
    errorInline,
    withAsterisk,
    ...rest
  } = props
  const errorId = useGlobalId('zb-formidable-error-')
  const elementRef = useRef()
  const [stateValue, setValue] = useState(value)

  const wrapperClass = classNames(
    'zb-control-wrap zb-radio-group-field',
    { 'has-error': error },
    className
  )

  const labelId = useGlobalId('zb-radio-group-field-')

  const labelOptions = {
    inputOptions: { 'aria-labelledby': labelId },
    hint,
    hintOptions,
    label,
    error,
    rules,
    errorOptions
  }
  const errorMessage = errorMessageShown(error)
    ? getError(error, errorOptions, errorId, errorInline)
    : null

  function handleRadioChange (value) {
    setValue(value)
    onChange(name, value)
  }

  function handleRadioBlur (value) {
    onBlur(name, value)
  }

  function handleRadioFocus (value) {
    onFocus(name, value)
  }

  /**
   * @param {import('./interfaces').RadioGroupFieldValue} option
   * @param {number} index
   */
  function renderRadio ({ disabled: itemDisabled, value, ...props }, i) {
    const childrenClassName = classNames({
      'zb-radio-button-is-error': !!error
    })
    return (
      <RadioField
        className={(disabled || !!itemDisabled) ? '' : childrenClassName}
        {...props}
        name={name}
        value={value}
        checked={value === stateValue}
        disabled={disabled || !!itemDisabled}
        key={i}
        onChange={() => handleRadioChange(value)}
        onBlur={() => handleRadioBlur(value)}
        onFocus={() => handleRadioFocus(value)} errorInline
      />
    )
  }

  useEffect(
    () => {
      setValue(value)
    },
    [value]
  )

  useUpdateRef(innerRef, elementRef, { ...props })

  return (
    <div
      className={wrapperClass}
      role='radiogroup'
      aria-labelledby={labelId}
      aria-invalid={!!error}
      aria-describedby={errorMessage ? errorMessage.props.id : null}
      aria-required={inputProps.required}
      {...rest}
    >
      {label ? (
        <Label {...labelOptions}>
          {label} {inputProps.required && withAsterisk && <sup className='zb-asterisk'>*</sup>}
        </Label>
      ) : null}
      <div className='zb-control-radio-group'>
        <input
          type='hidden'
          ref={elementRef}
          rules={rules}
          value={stateValue}
        />
        {values.map(renderRadio)}
      </div>
      {errorMessage}
    </div>
  )
}

function noop () {}

const RadioGroupField = forwardRef(radioGroupField)

RadioGroupField.displayName = 'RadioGroupField'

RadioGroupField.defaultProps = {
  values: [],
  value: '',
  withAsterisk: false,
  onFocus: noop,
  onChange: noop,
  onBlur: noop,
  errorMessageShown: error => Boolean(error)
}

RadioGroupField.propTypes = {
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.any,
  errorOptions: errorOptionsType,
  errorMessageShown: PropTypes.func,
  name: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(RadioField.propTypes.value),
  className: PropTypes.string,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  hint: PropTypes.string,
  hintOptions: hintOptionsType,
  errorInline: PropTypes.bool,
  withAsterisk: PropTypes.bool
}

export default RadioGroupField
